/**
 * Layout constants — the single source of truth for what artists draw against.
 *
 * The stage renders with `Scale.ENVELOP`: the design canvas is scaled to COVER
 * the viewport, so it always fills the screen and the overflow is cropped.
 * (The alternative, `Scale.FIT`, letterboxes — which put 77–91px black bars on
 * every modern phone and, worse, meant the DOM dialogue box sat at a different
 * fraction of the stage on every device. Art briefs written as "% of the canvas"
 * were therefore wrong by 5–6 points on the most common hardware.)
 *
 * Because ENVELOP crops, art must keep anything that matters inside a safe rect.
 * The numbers below are the worst case measured across the target device set,
 * and `npm run test:layout` re-derives them so they cannot drift.
 */

export const DESIGN_WIDTH = 1080;
export const DESIGN_HEIGHT = 1920;

/**
 * Widest and narrowest viewport aspect ratios we commit to supporting.
 * 0.39 ≈ a folded Galaxy Z cover screen; 0.75 ≈ a 4:3 tablet in portrait.
 */
export const MIN_ASPECT = 0.39;
export const MAX_ASPECT = 0.75;

/**
 * Crop-safe rect, in design pixels. Anything outside this can be cut off on
 * some supported device. Derived: at MIN_ASPECT the sides crop, at MAX_ASPECT
 * the top and bottom crop.
 */
export const SAFE = {
  left: 166,
  right: 914,
  top: 240,
  bottom: 1680,
  get width() { return this.right - this.left; },
  get height() { return this.bottom - this.top; },
} as const;

/**
 * The dialogue block is positioned in DOM space against the viewport, and the
 * viewport maps to the visible (post-crop) region. On a tall phone the visible
 * region is the full canvas height, so `bottom: 26%` reaches 499 design px up;
 * on a 4:3 tablet the visible region is 1440 design px tall and offset 240 down,
 * so the box reaches 614 design px up from the canvas bottom.
 *
 * Take the worst case: treat the bottom 615px as covered.
 */
export const DIALOGUE_COVERS_BOTTOM_PX = 615;

/**
 * Where a character's face must sit: inside the crop-safe rect AND clear of the
 * dialogue block. This is the rule that actually matters in an art brief.
 */
export const FACE_ZONE = {
  left: SAFE.left,
  right: SAFE.right,
  top: SAFE.top,
  bottom: DESIGN_HEIGHT - DIALOGUE_COVERS_BOTTOM_PX,
} as const;

/** Same rule as percentages, for briefs written against an arbitrary canvas. */
export const FACE_ZONE_PCT = {
  left: +(FACE_ZONE.left / DESIGN_WIDTH * 100).toFixed(1),
  right: +(FACE_ZONE.right / DESIGN_WIDTH * 100).toFixed(1),
  top: +(FACE_ZONE.top / DESIGN_HEIGHT * 100).toFixed(1),
  bottom: +(FACE_ZONE.bottom / DESIGN_HEIGHT * 100).toFixed(1),
} as const;

/** Visible region of the design canvas at a given viewport aspect, under ENVELOP. */
export function visibleRegion(aspect: number): { width: number; height: number } {
  const design = DESIGN_WIDTH / DESIGN_HEIGHT;
  return aspect < design
    ? { width: DESIGN_WIDTH * (aspect / design), height: DESIGN_HEIGHT }
    : { width: DESIGN_WIDTH, height: DESIGN_HEIGHT * (design / aspect) };
}
